import { trim, deburr } from 'lodash'

/*
 * the slash forces loading the module from userland, instead of platform
 * (e.g. node), which is needed to work around the eslint error:
 * https://github.com/mysticatea/eslint-plugin-node/blob/master/docs/rules/no-deprecated-api.md
 * (Note that userland modules don't hide core modules. For example,
 * `import punycode from "punycode"` still imports the deprecated core module even if you
 * executed yarn punycode. Use `import punycode from "punycode/"` to import userland
 * modules rather than core modules.)
 */
import punycode from 'punycode/'

import type { Schema } from '@wix/wix-data-schema-types'
import sanitize from '@wix/wix-data-client-dbsm-common/src/utils/sanitizeFieldKey'
import { generateUniqueName } from './generateUniqueName'
import { MAX_LENGTH_OF_COLLECTION_ID_AND_NAME } from '../consts'

const append = (string: string, suffix: string, max: number) =>
  string.length + suffix.length <= max
    ? string + suffix
    : string.slice(0, max - suffix.length) + suffix

const appendCounterToMakeUnique = (
  baseCollectionId: string,
  collections: Schema[],
) =>
  generateUniqueName(
    (prefix: string, i: number) =>
      append(prefix, i.toString(), MAX_LENGTH_OF_COLLECTION_ID_AND_NAME),
    baseCollectionId,
    collections.filter(({ isDeleted }) => !isDeleted).map(({ id }) => id),
    {
      allowNameWithoutSuffix: true,
      startIndex: 1,
      endIndex: Number.POSITIVE_INFINITY,
    },
  )

const hasOnlyASCIICharacters = (string: string) =>
  Array.from(string)
    .map(char => char.charCodeAt(0))
    .every(c => c < 128)

const convertNonAsciiToAscii = (string: string) => {
  string = deburr(string)
  return hasOnlyASCIICharacters(string) ? string : punycode.encode(string)
}

export const generateCollectionId = (
  collectionName: string,
  collections: Schema[],
) => {
  if (trim(collectionName).length === 0) {
    return ''
  }
  const sanitizedCollectionName = sanitize(collectionName, {
    mapWord: convertNonAsciiToAscii,
    allowHyphens: true,
    allowLeadingDigits: true,
  })
  return appendCounterToMakeUnique(
    sanitizedCollectionName.length === 0
      ? '_'
      : sanitizedCollectionName.slice(0, MAX_LENGTH_OF_COLLECTION_ID_AND_NAME),
    collections,
  )
}
